import type { ButtonProps as RacButtonProps } from 'react-aria-components';
import { Button as RacButton } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';

import styles from './index.module.scss';

type Sizes = 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large';
type Variants = 'primary' | 'secondary' | 'tertiary' | 'link';

type ButtonProps = {
  size?: Sizes;
  variant?: Variants;
  destructive?: boolean;
  iconLeading?: IconDefinition;
  iconTrailing?: IconDefinition;
  fullWidth?: boolean;
  /**
   * Allows focus on "disabled" buttons.
   *
   * Caution: you may need to handle click events manually when using this instead of `isDisabled`.
   */
  isVisuallyDisabled?: boolean;
} & RacButtonProps;

const sizeStyles: Record<Sizes, string> = {
  'x-small': styles.xSmall,
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
  'x-large': styles.xLarge,
  '2x-large': styles.x2Large,
};

const variantStyles: Record<Variants, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
  link: styles.link,
};

export const Button = (props: ButtonProps) => {
  const {
    children,
    size,
    variant,
    destructive = false,
    iconLeading,
    iconTrailing,
    fullWidth,
    isVisuallyDisabled,
    onPress,
    isDisabled,
    className = '',
    ...restProps
  } = props;

  const sizeStyle = sizeStyles[size ?? 'large'];
  const variantStyle = variantStyles[variant ?? 'primary'];
  const destructiveClass = destructive ? styles.destructive : '';
  const fullWidthClass = fullWidth ? styles.fullWidth : '';

  return (
    <RacButton
      className={`${styles.button} ${sizeStyle} ${variantStyle} ${destructiveClass} ${fullWidthClass} ${className}`}
      data-visually-disabled={isVisuallyDisabled || undefined}
      aria-disabled={isDisabled || isVisuallyDisabled}
      onPress={isVisuallyDisabled ? undefined : onPress}
      isDisabled={isDisabled}
      {...restProps}
    >
      <>
        {iconLeading && (
          <FontAwesomeIcon
            icon={iconLeading}
            className={styles.leadingIcon}
            aria-hidden
          />
        )}
        {children}
        {iconTrailing && (
          <FontAwesomeIcon
            icon={iconTrailing}
            className={styles.trailingIcon}
            aria-hidden
          />
        )}
      </>
    </RacButton>
  );
};
